import React, { useState, useEffect } from "react";
import axios from "axios";
import styled from "styled-components";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import { FaCloudArrowUp } from "react-icons/fa6";
import "react-notifications/lib/notifications.css";
import { FcAddImage } from "react-icons/fc";
import imageCompression from "browser-image-compression";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const Modal = styled.div`
  background-color: #f1f1f1;
  border-radius: 12px;
  padding: 20px;
  margin: 50px;
  width: 80%;
  max-width: 600px;
  height: auto;
  max-height: 80vh;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #888;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: bold;
`;

const ModalContent = styled.div`
  height: calc(100% - 110px);
  overflow-y: auto;
  position: relative;
`;

const ModalInput = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 10px;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #ddd;
  box-sizing: border-box;
`;

const CharCount = styled.div`
  font-size: 14px;
  color: #888;
  text-align: right;
`;

const FileInput = styled.input`
  display: none;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

const ModalButton = styled.button`
  background-color: #e75b82;
  color: #fff;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  padding: 10px 20px;
  transition: background-color 0.3s;
  font-size: 16px;

  &:hover {
    background-color: #005582;
  }
`;

const ImageUploadBox = styled.div`
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 20px;
  background-color: #f9f9f9;
  position: relative;

  &:hover {
    border-color: #888;
  }
`;

const UploadIcon = styled(FcAddImage)`
  font-size: 36px;
  margin-bottom: 10px;
`;

const UploadText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #888;
`;

const SelectedImageContainer = styled.div`
  position: relative;
  margin-bottom: 15px;
  max-height: 50vh; /* Limit the height of the container */
`;

const SelectedImage = styled.img`
  width: auto; /* Maintain aspect ratio */
  max-width: 100%;
  height: 100px; /* Set a default height */
  border-radius: 8px;
  cursor: pointer;
`;

const CancelIcon = styled.span`
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 20px;
  cursor: pointer;
  color: #888;
`;

const CategoryButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
`;

const CategoryButton = styled.button`
  background-color: ${(props) => (props.selected ? "#e75b82" : "#f9f9f9")};
  color: ${(props) => (props.selected ? "#fff" : "#333")};
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #e75b82;
    color: #fff;
  }

  &:focus {
    outline: none;
  }
`;

const AddPostModal = ({
  show,
  onClose,
  onSubmit,
  activeCategory,
  categories,
  setActiveCategory,
}) => {
  const [content, setContent] = useState("");
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState(
    activeCategory === "all-posts" ? "" : activeCategory
  );

  const addWatermark = async (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
  
        canvas.width = img.width;
        canvas.height = img.height;
  
        ctx.drawImage(img, 0, 0);
  
        const watermark = new Image();
        watermark.src = "./images/logo_only.png"; // Replace with your watermark path
        watermark.onload = () => {
          const watermarkWidth = img.width / 5;
          const watermarkHeight = (watermark.height / watermark.width) * watermarkWidth;
  
          const x = (canvas.width - watermarkWidth) / 2;
          const y = (canvas.height - watermarkHeight) / 2;
  
          ctx.globalAlpha = 0.07; // Adjust opacity for watermark effect (0.0 to 1.0)
          ctx.drawImage(watermark, x, y, watermarkWidth, watermarkHeight);
          ctx.globalAlpha = 1.0; // Reset opacity to default
  
          canvas.toBlob((blob) => {
            if (blob) {
              const watermarkedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(watermarkedFile);
            } else {
              reject(new Error("Canvas to Blob conversion failed"));
            }
          }, file.type);
        };
  
        watermark.onerror = (error) => {
          reject(error);
        };
      };
      img.onerror = (error) => {
        reject(error);
      };
    });
  };
  

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const options = {
          // maxSizeMB: 3,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          initialQuality: 0.3,
        };
        // console.log(`Original file size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
        const compressedFile = await imageCompression(file, options);
        // console.log(`Compressed file size: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
        setImage({
          file: compressedFile,
          url: URL.createObjectURL(compressedFile),
        });
      } catch (error) {
        console.error("Error compressing image:", error);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);
        setImage({
          file: compressedFile,
          url: URL.createObjectURL(compressedFile),
        });
      } catch (error) {
        console.error("Error compressing image:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // console.log(activeCategory, ' - ', category);
    if (!category && (!activeCategory || activeCategory==="all-posts")) {
      NotificationManager.warning("Please select any category!", "Warning");
      return;
    }
    if (!content && !image) {
      NotificationManager.warning(
        "Please write a caption or upload an image!",
        "Warning"
      );
      return;
    }
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("category_key", category?category:activeCategory);
    formData.append("content", content);
    if (image) {
      try {
        const watermarkedFile = await addWatermark(image.file);
        formData.append("image_path", watermarkedFile);
      } catch (error) {
        NotificationManager.error("Error adding watermark to image!", "Error");
        return;
      }
    }
  
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/community-posts`,
        // http://192.168.1.7:5002/v1/users/check_username_api  
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );  
      if (response.data && response.data.status === 201) {
        onSubmit({
          user_key: response.data.data.user_key,
          post_key: response.data.data.post_key,
          user_name: response.data.data.user_name,
          isApproved : response.data.data.isApproved,
          category_name: response.data.data.category_name,
          post_content: content,
          profile_image : response.data.data.image,
          image_path: image ? image.url : null,
          created_at: response.data.data.created_at,
          total_comments: response.data.data.total_comments,
          total_reactions:response.data.data.total_reactions,
          reaction_counts:response.data.data.reaction_counts,
        });
        setActiveCategory("my-posts");
        handleClose();
      } else {
        NotificationManager.error(response.data.message, "Error");
      }
    } catch (error) {
      NotificationManager.error(
        "Error creating post! Please try again!",
        "Error"
      );
      console.error("Error creating post:", error);
    }
    setLoading(false);
  };
  

  const handleCategoryChange = (category) => {
    setCategory(category);
  };

  const unselectImage = () => {
    setImage(null);
  };

  const handleClose = () => {
    setContent("");
    setImage(null);
    onClose();
  };

  if (!show) {
    return null;
  }

  return (
    <ModalOverlay>
      {loading && (
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      )}
      <Modal>
        <ModalHeader>
          <ModalTitle>Add Post</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>
        <ModalContent>
          <CategoryButtonContainer>
            {categories?.map((categoryItem) => (
              <CategoryButton
                key={categoryItem.category_key}
                selected={category === categoryItem.category_key}
                onClick={() =>
                  handleCategoryChange(categoryItem.category_key)
                }
              >
                {categoryItem.category_name}
              </CategoryButton>
            ))}
          </CategoryButtonContainer>
          <ImageUploadBox
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {image ? (
              <SelectedImageContainer>
                <SelectedImage src={image.url} alt="Selected Image" />
                <CancelIcon onClick={unselectImage}>×</CancelIcon>
              </SelectedImageContainer>
            ) : (
              <div onClick={() => document.getElementById("fileInput").click()}>
              <FaCloudArrowUp size={100}/>
            <p id="namefile">Only pics allowed! (jpg,jpeg,png)</p>
            <FileInput
              id="fileInput"
              type="file"
              value=""
              name="fileup"
              onChange={handleFileChange}
            />
            </div>
            )}
          </ImageUploadBox>
          <ModalInput
            placeholder="Write your caption here..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </ModalContent>
        <ModalActions>
          <ModalButton onClick={handleSubmit}>Post</ModalButton>
        </ModalActions>
      </Modal>
    </ModalOverlay>
  );
};

export default AddPostModal;
